import React, { useEffect, useMemo, useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase';
import { 
  ExclamationCircleIcon, 
  CheckIcon, 
  DownloadIcon 
} from '@heroicons/react/outline';
import useAnalytics from '../../../../hooks/useAnalytics';
import { isPdfReady, getPdfAvailability } from '../../../../utils/pdfAvailability';
import { buildExcludedSections } from '../documentsDashboardHelpers';
import { Tooltip } from 'react-tooltip';

/**
 * ReportBundleDialog
 * ------------------
 * Reusable dialog that allows clinicians to pick which generated report sections
 * to download as a single clinical assessment bundle, in either PDF or DOCX format.
 *
 * Key design points:
 * - Default selection includes all available and visible sections based on the
 *   current org/diagnosis gating already applied by the caller.
 * - Format toggle (PDF / DOCX) controls which storage path is checked for
 *   availability: ``pdfPath`` for PDF, ``currentPath`` for DOCX.
 * - Each section shows a status pill: Approved / Ready / Locked. Locked is disabled.
 * - If any selected section is currently processing, the Download action is disabled
 *   to avoid race conditions (the document may not be ready yet).
 * - Single-selection fast path is handled by the backend; we just pass sectionIds
 *   and the chosen format.
 */
export default function ReportBundleDialog({
  open,
  onOpenChange,
  patientId,
  patientDataModel,
  visibleSections, // [{ id, reportStage, status }]
  processingStatuses = {},
  // Optional dialog title — SLaM Report Builder passes "Download Sections";
  // standard orgs keep the historical Clinical Assessment Report wording.
  title = 'Download Clinical Assessment Report',
}) {
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();
  // Map of sectionId -> selected
  const [selectedMap, setSelectedMap] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Feature flag: set to true to expose the PDF/DOCX format selector.
  // Kept false for production while the DOCX bundle feature is tested.
  const ENABLE_DOCX_BUNDLE = false;

  // Output format — determines which storage path key is checked for
  // availability (pdfPath vs currentPath) and what the backend merges.
  // Locked to 'pdf' when DOCX bundle is disabled.
  const [selectedFormat, setSelectedFormat] = useState('pdf');

  // Backend section ID -> documents key mapping (must match backend allowlist)
  const sectionKeyMapping = useMemo(() => ({
    coverPage: 'coverPageReport',
    clinicalInterview: 'clinicalinterviewReport',
    semiStructuredInterview: 'semistructuredinterviewReport',
    // CYP ADHD sections — the child pathway's documents dashboard feeds this
    // dialog too, resolving to its own documents keys.
    cypAdhdClinicalInterview: 'cypAdhdCiReport',
    cypAdhdSemiStructuredInterview: 'cypAdhdSsiReport',
    cypAdhdDiagnosticDecision: 'cypAdhdDdReport',
    cypAdhdPostAssessmentDiscussion: 'cypAdhdPadReport',
    cypAdhdRatingScales: 'cypAdhdRatingScalesReport',
    diagnosticDecision: 'diagnosticdecisionReport',
    postAssessmentDiscussion: 'postassessmentdiscussionReport',
    attachmentsRatingScales: 'ratingScalesReport',
    qbCheck: 'qbcheckReport',
    qbTest: 'qbtestReport',
  }), []);

  // Compute per-section availability based on the selected output format.
  // PDF sections are stored under ``pdfPath``; DOCX sections under
  // ``currentPath``.  Re-evaluates whenever the format toggle changes so the
  // UI disables sections that don't have the requested format available.
  const availabilityMap = useMemo(() => {
    const docs = patientDataModel?.documents || {};
    const map = {};
    visibleSections.forEach(s => {
      const key = sectionKeyMapping[s.id];
      if (!key || !docs[key]) {
        map[s.id] = false;
        return;
      }
      // For PDF format, use the resilience-aware check (respects pdfStatus);
      // for DOCX, the DOCX is always stored even when the PDF fails, so a
      // simple truthiness check on currentPath is correct.
      if (selectedFormat === 'docx') {
        map[s.id] = !!docs[key].currentPath;
      } else {
        map[s.id] = isPdfReady(docs[key]);
      }
    });
    return map;
  }, [patientDataModel?.documents, sectionKeyMapping, visibleSections, selectedFormat]);

  // Build a list of approved sections that are excluded from the PDF bundle
  // because their PDF is not ready (pending retry or failed).  This is shown
  // as an informational notice so the clinician knows why a section is missing
  // from the download rather than silently dropping it.
  const excludedSections = useMemo(() => buildExcludedSections({
    selectedFormat,
    visibleSections,
    availabilityMap,
    documents: patientDataModel?.documents || {},
    sectionKeyMapping,
    getAvailability: getPdfAvailability,
  }), [selectedFormat, visibleSections, availabilityMap, patientDataModel?.documents, sectionKeyMapping]);

  // Default selection when dialog opens: all approved sections (and with PDFs)
  useEffect(() => {
    if (!open) return;
    setErrorMessage('');
    try {
      trackEvent(ANALYTICS_EVENTS.REPORT_BUNDLE_DIALOG_OPENED, {
        patientId,
        sectionCount: visibleSections.length,
        format: selectedFormat,
      });
    } catch (e) {
      // analytics are best-effort; ignore
    }
    const next = {};
    visibleSections.forEach(s => {
      const available = availabilityMap[s.id];
      next[s.id] = !!available && s.status === 'approved';
    });
    setSelectedMap(next);
  }, [open, visibleSections, availabilityMap, trackEvent, ANALYTICS_EVENTS, patientId, selectedFormat]);

  // Helper predicates for per-item status
  const isApproved = (s) => s.status === 'approved';
  const isReady = (s) => s.status === 'ready-for-review';
  const isLocked = (s) => s.status === 'locked';

  const selectedIds = useMemo(() => (
    visibleSections.filter(s => selectedMap[s.id]).map(s => s.id)
  ), [visibleSections, selectedMap]);

  // Helper to mirror DocumentsDashboard locked messaging
  const getLockedSectionText = (sectionId) => {
    switch (sectionId) {
      case 'coverPage':
        return 'Available after all other report sections are approved';
      case 'clinicalInterview':
      case 'cypAdhdClinicalInterview':
        return 'Available after Clinical Interview assessment module is completed';
      case 'semiStructuredInterview':
      case 'cypAdhdSemiStructuredInterview':
        return 'Available after Semi-Structured Interview assessment module is completed';
      case 'diagnosticDecision':
      case 'cypAdhdDiagnosticDecision':
        return 'Available after Diagnostic Decision assessment module is completed';
      case 'postAssessmentDiscussion':
        // The PAD report only takes its final positive/negative shape once the
        // formal Diagnostic Decision is recorded, so it unlocks off the DD.
        return 'Available after the Diagnostic Decision module is completed';
      default:
        return 'This section has not yet been completed';
    }
  };

  // If any selected section is currently processing, disable submission
  const hasSelectedProcessing = useMemo(() => (
    selectedIds.some(sectionId => {
      const key = sectionId === 'attachmentsRatingScales' ? 'ratingScales' : (sectionId === 'coverPage' ? 'coverPage' : sectionId);
      return processingStatuses[key] === 'processing';
    })
  ), [selectedIds, processingStatuses]);

  const anySelectedNonApproved = useMemo(() => (
    visibleSections.some(s => selectedMap[s.id] && s.status !== 'approved')
  ), [visibleSections, selectedMap]);

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      setErrorMessage('Select at least one available section to download.');
      return;
    }
    // Belt-and-suspenders: the download button in DocumentsDashboard is also
    // disabled during PR processing, but the dialog may already be open when
    // the PR CF chain starts.  This guard prevents the actual submission.
    if (processingStatuses?.patientReport === 'processing') {
      setErrorMessage('The Patient Report is still finalising. Please wait before downloading.');
      return;
    }
    if (hasSelectedProcessing) {
      setErrorMessage('One or more selected sections are still finalising. Please wait and try again.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    try {
      try {
        trackEvent(ANALYTICS_EVENTS.REPORT_BUNDLE_DOWNLOAD_STARTED, {
          patientId,
          sections: selectedIds,
          sectionCount: selectedIds.length,
          format: selectedFormat,
        });
      } catch (_) {}

      const fn = httpsCallable(functions, 'generateClinicalReportBundle');
      const result = await fn({ patientId, sectionIds: selectedIds, format: selectedFormat });
      const data = result?.data || {};

      if (data.status !== 'success') {
        const message = mapBackendErrorToMessage(data);
        throw new Error(message);
      }

      const downloadUrl = data.url || data?.urls?.pdf;
      const ext = selectedFormat === 'docx' ? 'docx' : 'pdf';
      const fallbackFilename = selectedIds.length === 1
        ? `Clinical_Assessment_Section.${ext}`
        : `Clinical_Assessment_Bundle.${ext}`;
      const filename = data.filename || fallbackFilename;
      if (!downloadUrl) {
        throw new Error('Download URL not provided by the server.');
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      onOpenChange(false);
      try {
        trackEvent(ANALYTICS_EVENTS.REPORT_BUNDLE_DOWNLOAD_SUCCEEDED, {
          patientId,
          sections: selectedIds,
          sectionCount: selectedIds.length,
          format: selectedFormat,
          mode: data.mode || (selectedIds.length === 1 ? 'single' : 'bundle'),
        });
      } catch (_) {}
    } catch (err) {
      console.error('Bundle download failed:', err);
      setErrorMessage(err?.message || 'Download failed. Please try again.');
      try {
        trackEvent(ANALYTICS_EVENTS.REPORT_BUNDLE_DOWNLOAD_FAILED, {
          patientId,
          sections: selectedIds,
          sectionCount: selectedIds.length,
          format: selectedFormat,
          errorCode: err?.code || 'unknown',
        });
      } catch (_) {}
    } finally {
      setIsSubmitting(false);
    }
  };

  // Map backend errors to user-friendly messages (granular handling)
  const mapBackendErrorToMessage = (dataOrError) => {
    // If we got a structured response with status: 'error'
    if (dataOrError && typeof dataOrError === 'object' && dataOrError.status === 'error') {
      const code = dataOrError.code || '';
      const msg = dataOrError.message || '';
      if (code.includes('UNAUTHENTICATED')) return 'You must be signed in to download reports.';
      if (code.includes('PERMISSION_DENIED')) return 'You do not have permission to download this report.';
      if (code.includes('NOT_FOUND')) return 'No valid documents were found for the selected sections in the chosen format.';
      if (code.includes('INVALID_ARGUMENT')) return msg || 'Your selection is not valid. Please adjust and try again.';
      if (code.includes('FAILED_PRECONDITION')) return msg || 'Your account is missing required permissions. Please contact your organisation administrator.';
      return msg || 'Failed to generate report bundle. Please try again.';
    }
    // If we got a thrown error object (e.g., Firebase functions error)
    const errMsg = dataOrError?.message || '';
    if (/permission/i.test(errMsg)) return 'You do not have permission to download this report.';
    if (/unauth/i.test(errMsg)) return 'You must be signed in to download reports.';
    if (/not[- ]found|no valid/i.test(errMsg)) return 'No valid documents were found for the selected sections in the chosen format.';
    if (/invalid|unknown section/i.test(errMsg)) return 'Your selection is not valid. Please adjust and try again.';
    if (/docx bundle generation failed/i.test(errMsg)) return 'DOCX bundle generation failed. Please try downloading as PDF instead.';
    return 'Download failed. Please try again.';
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-lg w-[95vw]">
          <AlertDialog.Title className="text-xl font-semibold mb-2">{title}</AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-500 mb-4">
            Choose which sections to include. Downloads can take up to ~1 minute. Please don’t refresh.
          </AlertDialog.Description>

          <VisuallyHidden.Root>
            <AlertDialog.Description>
              Report bundle selection dialog
            </AlertDialog.Description>
          </VisuallyHidden.Root>

          {/* Format selection — mirrors the Export Data dialog's radio-button pattern.
              Hidden behind ENABLE_DOCX_BUNDLE flag; defaults to PDF when hidden. */}
          {ENABLE_DOCX_BUNDLE && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Download Format
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { key: 'pdf', label: 'PDF Document' },
                    { key: 'docx', label: 'Word Document (editable)' },
                  ].map((fmt) => (
                    <button
                      key={fmt.key}
                      type="button"
                      onClick={() => setSelectedFormat(fmt.key)}
                      disabled={isSubmitting}
                      className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedFormat === fmt.key
                          ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      } disabled:opacity-50`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                        selectedFormat === fmt.key
                          ? 'border-violet-600'
                          : 'border-gray-300'
                      }`}>
                        {selectedFormat === fmt.key && (
                          <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                        )}
                      </div>
                      {fmt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-px bg-gray-200 mb-4" aria-hidden="true" />
            </>
          )}

          {/* Sections list - same order as DocumentsDashboard (no grouping) */}
          <div className="space-y-2 pr-1">
            {visibleSections.map(section => {
              const statusApproved = isApproved(section);
              const statusReady = isReady(section);
              const statusLocked = isLocked(section);
              const checked = !!selectedMap[section.id];

              // Tooltip content for non-approved sections
              const tooltipId = !statusApproved ? 'bundle-tooltip' : undefined;
              const tooltipContent = statusReady
                ? 'AI outputs still need to be reviewed and approved'
                : (statusLocked ? getLockedSectionText(section.id) : undefined);

              // Disabled semantics: only approved are selectable
              const isDisabledVisual = !statusApproved;

              // Handlers
              const handleClick = (e) => {
                if (!statusApproved) {
                  e.preventDefault();
                  return;
                }
                setSelectedMap(prev => ({ ...prev, [section.id]: !prev[section.id] }));
              };

              return (
                <button
                  key={section.id}
                  type="button"
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={tooltipContent}
                  aria-disabled={isDisabledVisual ? 'true' : undefined}
                  tabIndex={isDisabledVisual ? -1 : 0}
                  onClick={handleClick}
                  className={`w-full text-left inline-flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    statusApproved
                      ? (checked
                          ? 'bg-green-100 text-green-800 border border-green-300 shadow-sm'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200')
                      : 'bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
                      statusApproved
                        ? (checked ? 'bg-green-600 border-green-600' : 'border-gray-300 bg-white')
                        : 'border-gray-200 bg-gray-50'
                    }`}>
                      {statusApproved && checked && <CheckIcon className="w-3 h-3 text-white" />}
                    </div>
                    <span>{section.reportStage}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Non-approved notice */}
          {anySelectedNonApproved && (
            <div className="mt-3 text-xs text-gray-600">
              You’ve selected sections that are not approved. They’ll still be included if available.
            </div>
          )}

          {/* Excluded sections notice -- shown when approved sections cannot
              be included because their PDF is pending retry or failed.
              Same shape as the error banner below (icon + colour + text, so
              the meaning never rides on colour alone), in amber because this
              is an "attention" state rather than a failure of the download. */}
          {excludedSections.length > 0 && (
            <div className="mt-3 p-3 text-sm text-amber-700 bg-amber-50 rounded-md flex items-start gap-2">
              <ExclamationCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="font-semibold mb-1">
                  {excludedSections.length === 1
                    ? '1 approved section is excluded from this download:'
                    : `${excludedSections.length} approved sections are excluded from this download:`}
                </p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {excludedSections.map(({ id, name, reason }) => (
                    <li key={id}>
                      {name}: {reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Error banner */}
          {errorMessage && (
            <div className="mt-3 p-3 text-sm font-semibold text-red-700 bg-red-50 rounded-md flex items-center gap-2">
              <ExclamationCircleIcon className="w-4 h-4" />
              {errorMessage}
            </div>
          )}

          {/* Footer */}
          <div className="mt-5 flex justify-end">
            <div className="flex justify-end gap-2">
              <AlertDialog.Cancel asChild>
                <button type="button" disabled={isSubmitting} className="px-3 py-2 text-sm font-medium rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50">Cancel</button>
              </AlertDialog.Cancel>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || selectedIds.length === 0 || hasSelectedProcessing}
                className={`px-3 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                  (isSubmitting || selectedIds.length === 0 || hasSelectedProcessing)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-violet-700 hover:bg-violet-800'
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Preparing…
                  </div>
                ) : (
                  <div className="flex items-center">
                    <DownloadIcon className="h-4 w-4 mr-2" />
                    Download
                  </div>
                )}
              </button>
            </div>
          </div>
          {/* Shared tooltip for section hover messages */}
          <Tooltip
            id="bundle-tooltip"
            place="top"
            className="z-50 text-xs font-semibold"
            style={{
              maxWidth: '260px',
              whiteSpace: 'normal',
              backgroundColor: 'rgba(34, 34, 34, 1)',
              borderRadius: '12px',
              color: '#fff',
            }}
          />

        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}


